import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useBreakpointValue } from '@tyro/core';
import { useEffect } from 'react';
import { useContainerMargin } from '../../../hooks/use-container-margin';
import { useClassListSettings } from '../../../store/class-list-settings';
import { BulkEditSaveBar } from './bulk-edit-save-bar';
import { GroupColumn } from './group-column';
import { NavConfirmDialog } from './nav-confirm-dialog';
import {
  ListManagerProvider,
  type UseListManagerStateProps,
  useListManagerState,
} from './state';
import { UnassignedColumn } from './unassigned-column';

interface ListManagerProps {
  listKey: string;
  unassignedStudents: UseListManagerStateProps['unassignedStudents'];
  groups: UseListManagerStateProps['groups'];
  onBulkSave: UseListManagerStateProps['onBulkSave'];
  enableDuplicateStudents?: boolean;
  includeClassGroupName?: boolean;
  onIsDirtyChange?: (isDirty: boolean) => void;
  additionalSaveBarFields?: React.ReactNode;
}

function ListEndSpacer() {
  const { spacing } = useTheme();
  const space = useBreakpointValue({ base: 1, sm: 1, lg: 3 });
  const spaceValue = space ? spacing(space) : '1px';

  return (
    <Box>
      <Box sx={{ width: spaceValue, height: spaceValue }} />
    </Box>
  );
}

export function ListManagerInner({
  onIsDirtyChange,
  additionalSaveBarFields,
}: Pick<ListManagerProps, 'onIsDirtyChange' | 'additionalSaveBarFields'>) {
  const containerMargin = useContainerMargin();
  const { state, onDragStart, onDragEnd, editedState, selectedStudentIds } =
    useListManagerState();
  const { removeContainerMargin } = useClassListSettings();

  useEffect(() => {
    onIsDirtyChange?.(editedState.isEditing);
  }, [editedState.isEditing, onIsDirtyChange]);

  useEffect(() => {
    return monitorForElements({ onDrop: onDragEnd, onDragStart });
  }, [state, selectedStudentIds]);

  return (
    <>
      <Box sx={{ overflowY: 'hidden' }}>
        <Box>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={removeContainerMargin ? {} : { px: containerMargin }}
          >
            {state.map((group) =>
              group.id === 'unassigned' ? (
                <UnassignedColumn key={group.id} group={group} />
              ) : (
                <GroupColumn key={group.id} group={group} />
              ),
            )}
            <ListEndSpacer />
          </Stack>
        </Box>
      </Box>
      <BulkEditSaveBar additionalFields={additionalSaveBarFields} />
      <NavConfirmDialog isDirty={editedState.isEditing} />
    </>
  );
}

export function ListManager({
  listKey,
  unassignedStudents,
  groups,
  onBulkSave,
  enableDuplicateStudents = false,
  includeClassGroupName = false,
  onIsDirtyChange,
  additionalSaveBarFields,
}: ListManagerProps) {
  const { setCurrentGroupList } = useClassListSettings();

  useEffect(() => {
    setCurrentGroupList?.(groups);
  }, [groups]);

  return (
    <ListManagerProvider
      listKey={listKey}
      unassignedStudents={unassignedStudents}
      groups={groups}
      onBulkSave={onBulkSave}
      enableDuplicateStudents={enableDuplicateStudents}
      includeClassGroupName={includeClassGroupName}
    >
      <ListManagerInner
        onIsDirtyChange={onIsDirtyChange}
        additionalSaveBarFields={additionalSaveBarFields}
      />
    </ListManagerProvider>
  );
}
